/**
 * Translation Loader Service
 *
 * Loads translations from the static JSON bundle shipped with the app
 * (`src/assets/i18n/{lang}.json`). Those files are the SINGLE SOURCE OF
 * TRUTH for every UI string: they are edited in the repo, versioned in git
 * and served as build assets.
 *
 * There is intentionally NO Firestore / Cloud-Function path here. A key
 * renders exactly what the committed JSON says — no runtime overlay, no
 * cache versioning, no background revalidation. To change a string: edit
 * the JSON file and redeploy. This removes any possibility of drift between
 * "what the repo says" and "what the app shows".
 *
 * ngx-translate resolves dotted keys (`shared.actions.save`) against the
 * nested JSON natively, so the file is returned as-is — no flattening.
 */

import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateLoader, TranslationObject } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { DEFAULT_LANGUAGE, isLanguageSupported } from './language.config';
import { I18N_VERSION } from './i18n-version';

@Injectable({
  providedIn: 'root'
})
export class TranslationLoaderService implements TranslateLoader {
  private http = inject(HttpClient);

  /**
   * Required by the `TranslateLoader` interface — called by ngx-translate
   * whenever the active language changes. Resolves with the bundled
   * `assets/i18n/{lang}.json`, falling back to the default-language asset
   * (then to an empty object) if the file cannot be loaded.
   */
  getTranslation(lang: string): Observable<TranslationObject> {
    let language = lang;
    if (!isLanguageSupported(language)) {
      console.warn(`[i18n] Unsupported language: ${language}, falling back to ${DEFAULT_LANGUAGE}`);
      language = DEFAULT_LANGUAGE;
    }
    return this.loadAsset(language);
  }

  /**
   * Build the asset URL with a content-hash cache-buster.
   *
   * The bundles are served `immutable` for a year (firebase.json `/assets/**`)
   * but their URL is not fingerprinted, so without `?v=` a browser keeps a
   * stale copy long after new keys ship. `I18N_VERSION` is regenerated from the
   * JSON content at build time (`scripts/i18n/stamp-version.cjs`), so the URL
   * changes exactly when — and only when — a translation changes.
   */
  private assetUrl(language: string): string {
    return `assets/i18n/${language}.json?v=${I18N_VERSION}`;
  }

  private loadAsset(language: string): Observable<TranslationObject> {
    const url = this.assetUrl(language);
    return this.http.get<TranslationObject>(url).pipe(
      catchError(httpError => {
        console.error(`[i18n] Failed to load ${url}:`, httpError);
        if (language !== DEFAULT_LANGUAGE) {
          const fallbackUrl = this.assetUrl(DEFAULT_LANGUAGE);
          return this.http.get<TranslationObject>(fallbackUrl).pipe(
            catchError(() => {
              console.error('[i18n] No translations available — keys will render raw');
              return of({} as TranslationObject);
            })
          );
        }
        console.error('[i18n] No translations available — keys will render raw');
        return of({} as TranslationObject);
      })
    );
  }
}
